import type { FC } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { MDText } from 'i18n-react';

import { Button } from '@motorway/mw-highway-code';
import { Select } from '@motorway/mw-highway-code/pre-alpha';

import cypressIds from 'Components/cypress_ids.json';
import {
	DEFAULT_MAKE,
	DEFAULT_MODEL,
	isMakeSelectorDisabled,
	isModelSelectorDisabled,
	isSelectedOptionDefault,
	MAKE_SELECTOR_ID,
	MODEL_SELECTOR_ID,
} from 'Components/Insights/helpers/Filters.helpers';
import { getSelectedModelByName } from 'Components/Insights/helpers/Insights.helpers';
import { useMakesAndModels } from 'Components/Insights/hooks/useGetMakesAndModels';
import { useInsightsRouter } from 'Components/Insights/hooks/useInsightsRouter';
import { useBreakpoint } from 'Context/BreakpointsContext/BreakpointsContext';
import {
	triggerInsightsMakeDropdownSelectEvent,
	triggerInsightsModelDropdownSelectEvent,
	triggerInsightsSearchEvent,
} from 'Services/analytics/events';
import { useInsightsStore } from 'Stores/InsightsStore/InsightsStore';
import { applyCypressData } from 'Utilities/helpers';

import LocalTexts from '../../../../Insights.texts';

import styles from './MakeAndModelForm.module.scss';

const LocalT = new MDText(LocalTexts);

export const MakeAndModelForm: FC<{ isParentOpen: boolean }> = ({ isParentOpen }) => {
	const {
		make: { id: queryMake },
		model: { name: queryModel },
	} = useInsightsStore((state) => state);

	const [selectedMake, setSelectedMake] = useState(queryMake);
	const [selectedModel, setSelectedModel] = useState(queryModel);
	const { goToResultsPage } = useInsightsRouter();

	const { getMakeById, makes, models } = useMakesAndModels(selectedMake);

	const {
		maxWidth: { breakpointMobile },
	} = useBreakpoint();

	const areSelectedOptionsIncomplete = (): boolean => {
		return (
			(selectedMake === queryMake && selectedModel === DEFAULT_MODEL) ||
			(selectedMake === queryMake && selectedModel === queryModel)
		);
	};

	const onSearch = useCallback(async () => {
		const selectedMakeObject = getMakeById(selectedMake);
		const selectedModelObject = getSelectedModelByName(models, selectedModel);

		const makeSlug = selectedMakeObject?.slug;
		const modelSlug = selectedModel;

		if (!makeSlug) return;

		const makeName = selectedMakeObject?.name;
		const modelName = selectedModelObject?.namePretty;

		triggerInsightsSearchEvent({ location: 'results', vehicleName: `${makeName} ${modelName}` });

		await goToResultsPage({ make: makeSlug, model: modelSlug, persistSorting: true });
	}, [goToResultsPage, makes, selectedMake, selectedModel]);

	useEffect(() => {
		const searchOnDesktopSelect = () => {
			if (
				!breakpointMobile &&
				!isSelectedOptionDefault(selectedMake, selectedModel) &&
				!areSelectedOptionsIncomplete()
			) {
				void onSearch();
			}
		};

		searchOnDesktopSelect();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedModel]);

	useEffect(() => {
		const resetDefaultValuesOnParentOpen = (): void => {
			if (isParentOpen) {
				setSelectedMake(queryMake);
				setSelectedModel(queryModel);
			}
		};

		resetDefaultValuesOnParentOpen();
	}, [isParentOpen, queryMake, queryModel]);

	const onChangeMake = (e: React.ChangeEvent<HTMLSelectElement>) => {
		setSelectedMake(Number(e.target.value));
		setSelectedModel(DEFAULT_MODEL);

		triggerInsightsMakeDropdownSelectEvent('results');
	};

	const onChangeModel = (e: React.ChangeEvent<HTMLSelectElement>) => {
		setSelectedModel(e.target.value);

		triggerInsightsModelDropdownSelectEvent('results');
	};

	return (
		<div className={styles.formContainer}>
			{breakpointMobile && <h1 className={styles.drawerHeader}>{LocalT.translate('filters.makeAndModel')}</h1>}
			<div className={styles.selectContainer}>
				<Select
					fullWidth
					showLabel
					disabled={isMakeSelectorDisabled(makes)}
					formProps={{
						input: {
							onChange: onChangeMake,
							value: String(selectedMake),
							...applyCypressData(cypressIds.inputs.insightsMakeSelect),
						},
					}}
					id={MAKE_SELECTOR_ID}
					label={String(LocalT.translate('filters.make'))}
				>
					<option value={DEFAULT_MAKE}>{LocalT.translate('filters.defaultMake')}</option>
					{makes &&
						makes.length > 0 &&
						makes.map((make) => (
							<option key={make.slug} value={make.id}>
								{make.name}
							</option>
						))}
				</Select>
				<Select
					fullWidth
					showLabel
					disabled={isModelSelectorDisabled(selectedMake)}
					formProps={{
						input: {
							onChange: onChangeModel,
							value: selectedModel,
							...applyCypressData(cypressIds.inputs.insightsModelSelect),
						},
					}}
					id={MODEL_SELECTOR_ID}
					label={String(LocalT.translate('filters.model'))}
				>
					<option value={DEFAULT_MODEL}>{LocalT.translate('filters.defaultModel')}</option>
					{models &&
						models.length > 0 &&
						models.map((model) => (
							<option key={model.name} value={model.name}>
								{model.namePretty}
							</option>
						))}
				</Select>
			</div>
			{breakpointMobile && (
				<Button
					{...applyCypressData(cypressIds.buttons.insightsSearchButton)}
					disabled={isSelectedOptionDefault(selectedMake, selectedModel) || areSelectedOptionsIncomplete()}
					label={LocalT.translate('filters.search')}
					onClick={onSearch}
					size="medium"
				/>
			)}
		</div>
	);
};
